import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DrawerStateProps,
  MyAreaCommonConfigState,
  MyAreaPillsVisibility,
  MyAreaTooltipsVisibility,
  MyDrawerExcursionProps,
  PrePaidCardProps,
  HeadsUpMessagesConfig,
  ConfirmationDrawerProps,
} from '@msc-b2c/interfaces/interfaces';
import { CategoryOrderItem } from '@msc-b2c/interfaces/types';

const initialState: MyAreaCommonConfigState = {
  prePaidCardProps: {
    perChildLabel: '',
    perCabinLabel: '',
    perPackageLabel: '',
    perAdultLabel: '',
    chefLabel: '',
    foodTypeLabel: '',
    durationLabel: '',
    durationValueLabel: '',
    codeLabel: '',
    alreadyBookedLabel: '',
    dayLabel: '',
    dayAtSeaLabelValue: '',
    explorerPackageLabel: '',
    myPlanButtonLabel: '',
    myPlanAlreadyPurchasedButtonLabel: '',
    disableEmbarkAndDisembarkExcursions: false,
    cardPillConfigurations: [],
    promotionPriceEnabled: false,
    showSinglePromotionPrice: false,
    allInclusiveInCartErrorTitle: '',
    allInclusiveInCartErrorDescription: '',
    allInclusiveInCartErrorButtonText: '',
  },
  drawerProps: {
    closeDrawerIcon: '',
    limitedSeatsPlaceholder: '',
    drawerEditLabel: '',
    updatingStatusLabel: '',
    updatingStatusLabelIcon: '',
    passengersLabel: '',
    addonEligiblePassengersLabel: '',
    allPassengersLabel: '',
    allEligiblePassengersLabel: '',
    overviewLabel: '',
    difficultyLevelLabel: '',
    tourLanguageLabel: '',
    minimumParticipantsLabel: '',
    stateroomLabel: '',
    bannerColorPicker: '',
    fontColorPicker: '',
    otherLabel: '',
    alreadyInCartMessageLabel: '',
    alreadyInCartCtaLabel: '',
    alreadyInCartCtaLink: undefined,
    drawerPillConfigurations: [],
    limitedSeatsPlaceholderThreshold: 0,
    tooltipLabel: '',
    tooltipIcon: '',
    carModelLabelPlaceholder: '',
    carPlateLabelPlaceholder: '',
    validationRulesCarModelPlateContainer: [],
  },
  drawerExcursionProps: {
    drawerExcursionType: {
      overviewExcursionLabel: '',
      overviewExcursionDescription: '',
      displayActivityIcons: false,
      selectLanguageLabel: '',
      multilingualLabel: '',
      singleLanguageLabel: '',
      hoursLabel: '',
      addToCartButtonLabel: '',
      addToCartButtonIcon: '',
      addingToCartButtonLabel: '',
      addingToCartButtonIcon: '',
    },
  },
  headsUpMessagesConfig: {
    icon: '',
    title: '',
    paxNotApplicableDesc: '',
    allInclusiveDesc: '',
    couplePackageDesc: '',
    nonPersonalMainDesc: '',
    nonPersonalSecondaryDesc: '',
    combinedPackagesDesc: '',
    underSmallKidAgeThresholdDesc: '',
    hideAllInclusive: false,
    hideCouple: false,
    hideCombined: false,
  },
  confirmationDrawerProps: {
    passengersPricingText: '',
    title: '',
    titleIcon: '',
    viewCartButtonLabel: '',
    continueShoppingButtonLabel: '',
  },
  azureSession: {},
  categoryOrder: [],
  pillsVisibility: {},
  tooltipsVisibility: {},
  cardSortingList: [],
};

const myAreaCommonConfigSlice = createSlice({
  name: 'myAreaCommonConfig',
  initialState,
  reducers: {
    prePaidCardProps(state, action: PayloadAction<PrePaidCardProps>) {
      state.prePaidCardProps = action.payload;
    },
    drawerProps(state, action: PayloadAction<DrawerStateProps>) {
      state.drawerProps = action.payload;
    },
    drawerExcursionProps(state, action: PayloadAction<MyDrawerExcursionProps>) {
      state.drawerExcursionProps = action.payload;
    },
    confirmationDrawerProps(state, action: PayloadAction<ConfirmationDrawerProps>) {
      state.confirmationDrawerProps = action.payload;
    },
    headsUpMessagesProps(state, action: PayloadAction<HeadsUpMessagesConfig>) {
      state.headsUpMessagesConfig = action.payload;
    },
    prepaidCardType(state, action: PayloadAction<string>) {
      state.prepaidType = action.payload;
    },
    azureSession(state, action: PayloadAction<object>) {
      state.azureSession = action.payload;
    },
    categoryOrderUpdate(state, action: PayloadAction<CategoryOrderItem[]>) {
      state.categoryOrder = action.payload;
    },
    cardSortingList(state, action: PayloadAction<string[]>) {
      state.cardSortingList = action.payload;
    },
    pillsVisibility(state, action: PayloadAction<MyAreaPillsVisibility>) {
      state.pillsVisibility = {
        ...state.pillsVisibility,
        ...action.payload,
      };
    },
    tooltipsVisibility(state, action: PayloadAction<MyAreaTooltipsVisibility>) {
      state.tooltipsVisibility = {
        ...state.tooltipsVisibility,
        ...action.payload,
      };
    },
  },
});

export const {
  prePaidCardProps,
  drawerProps,
  drawerExcursionProps,
  headsUpMessagesProps,
  prepaidCardType,
  azureSession,
  categoryOrderUpdate,
  pillsVisibility,
  tooltipsVisibility,
  cardSortingList,
  confirmationDrawerProps,
} = myAreaCommonConfigSlice.actions;
export default myAreaCommonConfigSlice.reducer;
